/**
 * The Solar Hijri (Persian, Jalali) calendar as Iran uses it: astronomical.
 *
 * The calendar's structure follows Reingold and Dershowitz, *Calendrical Calculations: The
 * Ultimate Edition* (Cambridge University Press, 2018), in its chapter on the Persian
 * calendar. Iran's official calendar
 * is not a cycle but an observation: the year begins on the day of the March (vernal)
 * equinox, and a day is "the equinox day" when the equinox falls before noon on the Iran
 * Standard Time meridian (52.5° E, UTC+3:30); an equinox after that noon starts the year the
 * next day. Months 1–6 have 31 days, months 7–11 have 30, and Esfand has 29 days, or 30 in
 * a leap year (a year of 366 days, i.e. when the next Nowruz is 366 days after this one).
 *
 * The equinox instant comes from Jean Meeus, *Astronomical Algorithms* (2nd ed., Willmann-
 * Bell, 1998), chapter 27: a polynomial for the mean equinox plus 24 periodic terms, good to
 * a few minutes for 1000–3000 CE. Terrestrial Time is turned into Universal Time with the
 * ΔT polynomials of Espenak and Meeus (NASA). That is accurate enough to decide the
 * Nowruz day except in the rare year when the equinox falls within minutes of noon, and
 * the future depends on ΔT, which cannot be predicted beyond a few seconds a year ahead.
 *
 * The widely used *arithmetic* approximation — a 33-year cycle in which year y is a leap
 * year when (25y + 11) mod 33 < 8 — is included for comparison. It is what the date
 * library in browsers uses. Checked against this file's astronomical calendar it agrees
 * for every year from 1178 to 1634 (1799–2255 CE) and first differs at 1635 (2256 CE); the
 * tests pin both facts.
 */

import { mod, rdFromGregorian } from "@/lib/tool-kit/calendars/rd";

export const PERSIAN_FIRST_GREGORIAN_YEAR = 622;
/** Iran Standard Time, in days. */
const TEHRAN_OFFSET_DAYS = 3.5 / 24;
/** R.D. 0 (Sunday 31 December 1 BCE, Gregorian) is JD 1 721 424.5. */
const JD_OF_RD_ZERO = 1_721_424.5;

/** Meeus Table 27.C: amplitude, phase (degrees) and rate (degrees per century). */
const EQUINOX_TERMS: readonly (readonly [number, number, number])[] = [
  [485, 324.96, 1934.136],
  [203, 337.23, 32_964.467],
  [199, 342.08, 20.186],
  [182, 27.85, 445_267.112],
  [156, 73.14, 45_036.886],
  [136, 171.52, 22_518.443],
  [77, 222.54, 65_928.934],
  [74, 296.72, 3034.906],
  [70, 243.58, 9037.513],
  [58, 119.81, 33_718.147],
  [52, 297.17, 150.678],
  [50, 21.02, 2281.226],
  [45, 247.54, 29_929.562],
  [44, 325.15, 31_555.956],
  [29, 60.93, 4443.417],
  [18, 155.12, 67_555.328],
  [17, 288.79, 4562.452],
  [16, 198.04, 62_894.029],
  [14, 199.76, 31_436.921],
  [12, 95.39, 14_577.848],
  [12, 287.11, 31_931.756],
  [12, 320.81, 34_777.259],
  [9, 227.73, 1222.114],
  [8, 15.45, 16_859.074],
];

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/** Julian Ephemeris Day (Terrestrial Time) of the March equinox of a Gregorian year. */
export function marchEquinoxJde(year: number): number {
  let mean: number;
  if (year < 1000) {
    const y = year / 1000;
    mean =
      1_721_139.291_89 +
      365_242.1374 * y +
      0.061_34 * y ** 2 +
      0.001_11 * y ** 3 -
      0.000_71 * y ** 4;
  } else {
    const y = (year - 2000) / 1000;
    mean =
      2_451_623.809_84 +
      365_242.374_04 * y +
      0.051_69 * y ** 2 -
      0.004_11 * y ** 3 -
      0.000_57 * y ** 4;
  }
  const t = (mean - 2_451_545) / 36_525;
  const w = 35_999.373 * t - 2.47;
  const delta =
    1 + 0.0334 * Math.cos(toRadians(w)) + 0.0007 * Math.cos(toRadians(2 * w));
  let sum = 0;
  for (const [a, b, c] of EQUINOX_TERMS) {
    sum += a * Math.cos(toRadians(b + c * t));
  }
  return mean + (0.000_01 * sum) / delta;
}

/** ΔT = TT − UT in seconds (Espenak and Meeus, NASA polynomial expressions). */
export function deltaTSeconds(year: number): number {
  if (year < 500) {
    const u = year / 100;
    return (
      10_583.6 -
      1014.41 * u +
      33.783_11 * u ** 2 -
      5.952_053 * u ** 3 -
      0.179_845_2 * u ** 4 +
      0.022_174_192 * u ** 5 +
      0.009_031_652_1 * u ** 6
    );
  }
  if (year < 1600) {
    const u = (year - 1000) / 100;
    return (
      1574.2 -
      556.01 * u +
      71.234_72 * u ** 2 +
      0.319_781 * u ** 3 -
      0.850_346_3 * u ** 4 -
      0.005_050_998 * u ** 5 +
      0.008_357_207_3 * u ** 6
    );
  }
  if (year < 1700) {
    const t = year - 1600;
    return 120 - 0.9808 * t - 0.015_32 * t ** 2 + t ** 3 / 7129;
  }
  if (year < 1800) {
    const t = year - 1700;
    return (
      8.83 +
      0.1603 * t -
      0.005_928_5 * t ** 2 +
      0.000_133_36 * t ** 3 -
      t ** 4 / 1_174_000
    );
  }
  if (year < 1860) {
    const t = year - 1800;
    return (
      13.72 -
      0.332_447 * t +
      0.006_861_2 * t ** 2 +
      0.004_111_6 * t ** 3 -
      0.000_374_36 * t ** 4 +
      0.000_012_127_2 * t ** 5 -
      0.000_000_169_9 * t ** 6 +
      0.000_000_000_875 * t ** 7
    );
  }
  return deltaTModern(year);
}

function deltaTModern(year: number): number {
  if (year < 1900) {
    const t = year - 1860;
    return (
      7.62 +
      0.5737 * t -
      0.251_754 * t ** 2 +
      0.016_806_68 * t ** 3 -
      0.000_447_362_4 * t ** 4 +
      t ** 5 / 233_174
    );
  }
  if (year < 1920) {
    const t = year - 1900;
    return (
      -2.79 +
      1.494_119 * t -
      0.059_893_9 * t ** 2 +
      0.006_196_6 * t ** 3 -
      0.000_197 * t ** 4
    );
  }
  if (year < 1941) {
    const t = year - 1920;
    return 21.2 + 0.844_93 * t - 0.0761 * t ** 2 + 0.002_093_6 * t ** 3;
  }
  if (year < 1961) {
    const t = year - 1950;
    return 29.07 + 0.407 * t - t ** 2 / 233 + t ** 3 / 2547;
  }
  if (year < 1986) {
    const t = year - 1975;
    return 45.45 + 1.067 * t - t ** 2 / 260 - t ** 3 / 718;
  }
  if (year < 2005) {
    const t = year - 2000;
    return (
      63.86 +
      0.3345 * t -
      0.060_374 * t ** 2 +
      0.001_727_5 * t ** 3 +
      0.000_651_814 * t ** 4 +
      0.000_023_735_99 * t ** 5
    );
  }
  if (year < 2050) {
    const t = year - 2000;
    return 62.92 + 0.322_17 * t + 0.005_589 * t ** 2;
  }
  if (year < 2150) {
    return -20 + 32 * ((year - 1820) / 100) ** 2 - 0.5628 * (2150 - year);
  }
  return -20 + 32 * ((year - 1820) / 100) ** 2;
}

export interface Equinox {
  /** Julian Day in Universal Time. */
  jdUt: number;
  /** Fixed day (R.D.) of the Tehran civil date the equinox falls on. */
  tehranDay: number;
  /** Whole minutes after midnight, Iran Standard Time (UTC+3:30). */
  tehranMinutes: number;
  /** R.D. of the UTC date, and whole minutes after UTC midnight. */
  utcDay: number;
  utcMinutes: number;
}

/** When the March equinox of a Gregorian year happens, in UT and in Tehran. */
export function marchEquinox(year: number): Equinox {
  const jdUt = marchEquinoxJde(year) - deltaTSeconds(year) / 86_400;
  const rdUt = jdUt - JD_OF_RD_ZERO;
  const rdTehran = rdUt + TEHRAN_OFFSET_DAYS;
  const utcDay = Math.floor(rdUt);
  const tehranDay = Math.floor(rdTehran);
  return {
    jdUt,
    utcDay,
    utcMinutes: Math.floor((rdUt - utcDay) * 1440),
    tehranDay,
    tehranMinutes: Math.floor((rdTehran - tehranDay) * 1440),
  };
}

const NOON_MINUTES = 12 * 60;

/** R.D. of 1 Farvardin of the Persian year that begins in Gregorian year `year`. */
export function nowruzInGregorianYear(year: number): number {
  const equinox = marchEquinox(year);
  return equinox.tehranMinutes < NOON_MINUTES
    ? equinox.tehranDay
    : equinox.tehranDay + 1;
}

/** R.D. of 1 Farvardin of a Persian year (astronomical). */
export function persianNewYear(year: number): number {
  return nowruzInGregorianYear(year + PERSIAN_FIRST_GREGORIAN_YEAR - 1);
}

export function daysInPersianYear(year: number): number {
  return persianNewYear(year + 1) - persianNewYear(year);
}

export function isPersianLeapYear(year: number): boolean {
  return daysInPersianYear(year) === 366;
}

export interface PersianDate {
  year: number;
  month: number;
  day: number;
}

export function persianMonthLength(year: number, month: number): number {
  if (month <= 6) {
    return 31;
  }
  if (month <= 11) {
    return 30;
  }
  return isPersianLeapYear(year) ? 30 : 29;
}

/** Days before the first of a month within its year. */
function daysBeforeMonth(month: number): number {
  return month <= 7 ? 31 * (month - 1) : 30 * (month - 1) + 6;
}

export function fixedFromPersian(date: PersianDate): number {
  return persianNewYear(date.year) + daysBeforeMonth(date.month) + date.day - 1;
}

export function persianFromFixed(rd: number): PersianDate {
  // Farvardin 1 of the year containing rd falls in Gregorian year (that year + 621).
  const gregorianYear = Math.floor((rd - 1) / 365.2425) + 1;
  let year = gregorianYear - PERSIAN_FIRST_GREGORIAN_YEAR + 1;
  while (persianNewYear(year + 1) <= rd) {
    year++;
  }
  while (persianNewYear(year) > rd) {
    year--;
  }
  const dayOfYear = rd - persianNewYear(year);
  const month =
    dayOfYear < 186
      ? Math.floor(dayOfYear / 31) + 1
      : Math.floor((dayOfYear - 186) / 30) + 7;
  return { year, month, day: dayOfYear - daysBeforeMonth(month) + 1 };
}

export function isValidPersianDate(date: PersianDate): boolean {
  const { year, month, day } = date;
  return (
    Number.isInteger(year) &&
    Number.isInteger(month) &&
    Number.isInteger(day) &&
    year >= 1 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= persianMonthLength(year, month)
  );
}

/* ------------------------------------------------------------------ */
/* The 33-year arithmetic approximation                                */
/* ------------------------------------------------------------------ */

export function isArithmeticLeapYear(year: number): boolean {
  return mod(25 * year + 11, 33) < 8;
}

/** Leap years among 1 … year−1 in the 33-year cycle (eight in every 33). */
function arithmeticLeapsBefore(year: number): number {
  const before = year - 1;
  const cycles = Math.floor(before / 33);
  let leaps = cycles * 8;
  for (let y = cycles * 33 + 1; y <= before; y++) {
    if (isArithmeticLeapYear(y)) {
      leaps++;
    }
  }
  return leaps;
}

/**
 * R.D. of 1 Farvardin of year 1, chosen so that 1 Farvardin 1400 is 21 March 2021 (the
 * value the date library in browsers also gives).
 */
export const ARITHMETIC_EPOCH =
  rdFromGregorian(2021, 3, 21) - 365 * 1399 - arithmeticLeapsBefore(1400);

/** R.D. of 1 Farvardin of a year in the 33-year arithmetic calendar. */
export function arithmeticNewYear(year: number): number {
  return ARITHMETIC_EPOCH + 365 * (year - 1) + arithmeticLeapsBefore(year);
}

export function arithmeticFromFixed(rd: number): PersianDate {
  let year = Math.floor((rd - ARITHMETIC_EPOCH) / 365.2424) + 1;
  while (arithmeticNewYear(year + 1) <= rd) {
    year++;
  }
  while (arithmeticNewYear(year) > rd) {
    year--;
  }
  const dayOfYear = rd - arithmeticNewYear(year);
  const month =
    dayOfYear < 186
      ? Math.floor(dayOfYear / 31) + 1
      : Math.floor((dayOfYear - 186) / 30) + 7;
  return { year, month, day: dayOfYear - daysBeforeMonth(month) + 1 };
}
